import { UseMutationResult } from '@tanstack/react-query'
import { IInvoice, InvoiceFiles } from 'models/invoices.models'
import { selectActiveWorkspace } from 'lib/helpers/selectors'
import useDialog from 'hooks/Shared/useDialog'
import useCancelCFDI from 'hooks/Invoices/CFDIs/useCancelCFDI'
import useChangeCFDIPaymentStatus from 'hooks/Invoices/CFDIs/useChangeCFDIPaymentStatus'
import useDownloadInvoiceFiles, {
  IUseDownloadInvoiceFilesVariables,
} from 'hooks/Invoices/CFDIs/useDownloadInvoiceFiles'
import useDownloadInvoicesFiles, {
  IUseDownloadInvoicesFilesVariables,
} from 'hooks/Invoices/CFDIs/useDownloadInvoicesFiles'
import useSendInvoiceByEmail from 'hooks/Invoices/CFDIs/useSendInvoiceByEmail'
import useSendCreditNoteByEmail from 'hooks/Invoices/CFDIs/useSendCreditNoteByEmail'
import useSendPaymentComplementByEmail from 'hooks/Invoices/CFDIs/useSendPaymentComplementByEmail'
import { useAppSelector } from 'store/hooks'

import { TaskAlt } from 'components/Shared/CustomIcons'
import { ICFDIsContainerState } from './useCFDIsContainer'
import { IUseCFDIsContainerQueries } from './useCFDIsContainerQueries'

interface IUseCFDIsContainerMutationsProps
  extends Pick<IUseCFDIsContainerQueries, 'refetchInvoices'> {
  updateState: (update: (draft: ICFDIsContainerState) => void) => void
}

interface IUseCFDIsContainerMutations {
  cancelCfdiMutation: UseMutationResult<
    void,
    any,
    {
      invoice: IInvoice
      reason: number
      substitute?: string
    },
    unknown
  >
  changeCfdiPaymentStatusMutation: UseMutationResult<
    void,
    any,
    {
      invoice: IInvoice
      status: string
    },
    unknown
  >
  downloadInvoiceFilesMutation: UseMutationResult<
    InvoiceFiles,
    any,
    IUseDownloadInvoiceFilesVariables,
    unknown
  >
  downloadInvoicesFilesMutation: UseMutationResult<
    string,
    any,
    IUseDownloadInvoicesFilesVariables,
    unknown
  >
  sendCfdiByEmailMutation: UseMutationResult<void, any, IInvoice, unknown>
  sendCreditNoteByEmailMutation: UseMutationResult<
    void,
    any,
    {
      creditNote: any
      invoice: IInvoice
    },
    unknown
  >
  sendPayComplementByEmailMutation: UseMutationResult<
    void,
    any,
    {
      payComplement: any
      invoice: IInvoice
    },
    unknown
  >
}

const useCFDIsContainerMutations = ({
  refetchInvoices,
  updateState,
}: IUseCFDIsContainerMutationsProps): IUseCFDIsContainerMutations => {
  const dialog = useDialog()

  const activeWorkspace = useAppSelector(selectActiveWorkspace)

  const cancelCfdiMutation = useCancelCFDI(activeWorkspace, {
    onSuccess: () => {
      refetchInvoices()
      updateState(draft => {
        draft.cancelCFDIDialogOpen = false
        draft.validDialog = false
      })
    },
  })

  const changeCfdiPaymentStatusMutation = useChangeCFDIPaymentStatus(
    activeWorkspace,
    {
      onSuccess: () => refetchInvoices(),
    }
  )

  const downloadInvoiceFilesMutation = useDownloadInvoiceFiles({
    onSuccess: () =>
      updateState(draft => {
        draft.seeMoreAnchor = null
      }),
  })

  const downloadInvoicesFilesMutation = useDownloadInvoicesFiles()

  const sendCfdiByEmailMutation = useSendInvoiceByEmail(activeWorkspace, {
    onSuccess: async () => {
      refetchInvoices()
      await dialog.primary({
        title: '¡Listo!',
        subtitle: 'El CFDI seleccionado fue enviado a tu correo.',
        icon: TaskAlt,
      })
      updateState(draft => {
        draft.seeMoreAnchor = null
      })
    },
  })

  const sendCreditNoteByEmailMutation = useSendCreditNoteByEmail(
    activeWorkspace,
    {
      onSuccess: () =>
        dialog.primary({
          title: '¡Listo!',
          subtitle:
            'El Complemento de pago seleccionado fue enviado a tu correo.',
          icon: TaskAlt,
        }),
    }
  )

  const sendPayComplementByEmailMutation = useSendPaymentComplementByEmail(
    activeWorkspace,
    {
      onSuccess: () =>
        dialog.primary({
          title: '¡Listo!',
          subtitle:
            'El Complemento de pago seleccionado fue enviado a tu correo.',
          icon: TaskAlt,
        }),
    }
  )

  return {
    cancelCfdiMutation,
    changeCfdiPaymentStatusMutation,
    downloadInvoiceFilesMutation,
    downloadInvoicesFilesMutation,
    sendCfdiByEmailMutation,
    sendCreditNoteByEmailMutation,
    sendPayComplementByEmailMutation,
  }
}

export default useCFDIsContainerMutations
